<template>
  <section class="explore-residents">
    <div v-if="graphData" class="grid gap-3 grid-cols-1 sm-grid-cols-2">
      <div v-for="(item, i) in graphData" class="flex outline corner vertical large top p-y-3 p-x-3" :key="item.title">
        <div class="f5 text-bold">{{  item.title }}</div>
        <div class="explore-residents__container">
          <mvtChart class="explore-residents__graph" :data="{
            type: 'doughnut',
            options: {
              aspectRatio: 1,
              maintainAspectRatio: false,
              cutoutPercentage: 80,
              animation: {
                animateRotate: false,
              },
              legend: {
                display: false,
              },
              tooltips: {
                callbacks: {
                  label: function(tooltipItem, data) {
                    const dataset = data.datasets[tooltipItem.datasetIndex];
                    const labels = data.labels;
                    return `${labels[tooltipItem.index]}: ${dataset.data[tooltipItem.index]}%`;
                  }
                }
              }
            },
            data: {
              labels: item.data.map(set => set.label),
              datasets: [{
                label: item.title,
                data: item.data.map(set => set.value),
                backgroundColor: colors,
                hoverOffset: 4
              }]
            }
          }" />
          <div class="explore-residents__list">
            <ul >
              <li v-for="(data, i) in item.data" :key="data.label" class="explore-residents__list-item">
                <span class="dot" :style="{ backgroundColor: colors[i] || 'gray'}"></span><span class="text-bold">{{ data.value }}%</span> {{ data.label }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import mvtChart from '@/common/components/mvtchart/mvt-chart.vue';

const mappings = [
  {
    title: 'Household Income',
    key: 'income',
    data: [
      {
        label: '$200,000+',
        values: [
          '200k_OVER'
        ]
      },
      {
        label: '$150,000-$199,000',
        values: [
          '150k_200k'
        ]
      },
      {
        label: '$100,000-$149,000',
        values: [
          '100k_125k',
          '125k_150k'
        ]
      },
      {
        label: '$75,000-$99,999',
        values: [
          '75k_100k'
        ]
      },
      {
        label: '$50,000-$74,000',
        values: [
          '50k_60k',
          '60k_75k'
        ]
      },
      {
        label: '<$49,999',
        values: [
          '45k_50k',
          '40k_45k',
          '35k_40k',
          '30k_35k',
          '25k_30k',
          '20k_25k',
          '15k_20k',
          '10k_15k',
        ]
      }
    ]
  },
  {
    title: 'Education Levels',
    key: 'education',
    data: [
      {
        label: 'PhD/Doctorate',
        values: [
          'doctorate_degree'
        ]
      },
      {
        label: "Master's",
        values: [
          'masters_degree',
          'professional_degree',
        ]
      },
      {
        label: "Bachelor's",
        values: [
          'bachelors_degree'
        ]
      },
      {
        label: "Associate's",
        values: [
          'associates_degree',
        ]
      },
      {
        label: 'HS Diploma',
        values: [
          'high_school_diploma',
          'some_college',
        ]
      },
      {
        label: 'Other',
        values: [
          'no_high_school',
          'some_high_school'
        ]
      },
    ]
  },
  {
    title: 'Age Distribution',
    key: 'age',
    data: [
      {
        label: '65+ yrs',
        values: [
          '65_74',
          '75_84',
          '85_OVER',
        ]
      },
      {
        label: '45-64 yrs',
        values: [
          '45_54',
          '55_64'
        ]
      },
      {
        label: '35-44 yrs',
        values: [
          '35_44',
        ]
      },
      {
        label: '19-34 yrs',
        values: [
          '20_24',
          '25_34',
        ]
      },
      {
        label: '< 18 yrs',
        values: [
          '15_19',
          '10_14',
          '5_9'
        ]
      },
    ]
  },
  {
    title: 'Housing',
    key: 'housing',
    data: [
      {
        label: 'Owner Occupied',
        values: [
          'owner_occupied',
        ]
      },
      {
        label: 'Renter Occupied',
        values: [
          'renter_occupied'
        ]
      },
      {
        label: 'Vacant',
        values: [
          'vacant',
        ]
      },
    ]
  },
]

export default {
  name: 'exploreResidents',
  components: {
    mvtChart,
  },
  props: {
    demographics: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      colors: [
          '#0E6959',
          '#38003E',
          '#DB2165',
          '#EBB600',
          '#ABE629',
          '#B51851'
      ],
    }
  },
  computed: {
    graphData() {

      if(!this.demographics) {
        return null;
      }

      return mappings.map(set => {

        const data = set.data.map(element => {

          const value = element.values.reduce((acc, curr) => acc + this.demographics[set.key][curr], 0)

          return {
            label: element.label,
            value,
          }

        });

        const total = data.reduce((acc, curr) => acc + curr.value, 0);

        return {
          title: set.title,
          data: data.map(el => {
            const percentage = Math.round(((el.value / total) * 100));
            return {
              ...el,
              value: percentage,
            }
          }),
          total,
        }

      });

    },
  }
};
</script>

<style lang="scss">
@use "~@/common/scss/base/_defined.scss" as *;

.explore-residents {
  &__container{
    display: grid;
    grid-template-columns:  minmax(100px, 50%) minmax(180px, 30%);
  }
  &__list {
    align-self: center;
    justify-self: center;
    ul{
      display: flex;
      align-items: flex-start;
      text-align: left;
      flex-direction: column;
      gap: var(--g1);
    }

    &-item {
      position: relative;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      gap: var(--g0);
      white-space: nowrap;

      .dot {
        top: 50%;
        right: 100%;
        transform: translate3d(-5px, -50%, 0);
        position: absolute;
        width: 8px;
        height: 8px;
        border-radius: 50%;
      }

    }

    @media screen and (max-width: $screen-sm-max) {
      @include f7();
    }

  }

  &__graph {
      align-self: center;
      justify-self: center;
      max-height: 180px;
      max-width: 180px;
      width: 100%;
      height: 100%;
      position: relative;
  }

}

</style>